import { useEffect, useState } from "react";
import { Smartphone, X, Download } from "lucide-react";

/**
 * Bannière d'invitation à installer la PWA.
 * - Apparait automatiquement 15s après le chargement si l'app n'est pas déjà installée
 *   et que l'utilisateur n'a pas déjà refusé.
 * - Chrome/Android : utilise l'événement beforeinstallprompt natif.
 * - iOS Safari : affiche un bandeau d'instructions (pas d'API d'install).
 */
export default function InstallPWABanner() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem("proxiPwaDismissed") === "1") return;
    } catch { /* ignore */ }
    // Standalone détection (déjà installé)
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator?.standalone === true;
    if (standalone) return;

    const ua = window.navigator.userAgent || "";
    const ios = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
    setIsIOS(ios);

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setTimeout(() => setVisible(true), 15000);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS : pas d'event, on affiche quand même après 15s
    if (ios) {
      setTimeout(() => setVisible(true), 15000);
    }
    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  const close = () => {
    setVisible(false);
    try { localStorage.setItem("proxiPwaDismissed", "1"); } catch { /* ignore */ }
  };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    setVisible(false);
    try { localStorage.setItem("proxiPwaDismissed", "1"); } catch { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 inset-x-4 md:left-auto md:right-4 md:w-96 z-50 bg-white rounded-2xl border border-slate-200 shadow-xl p-4"
         data-testid="pwa-install-banner">
      <button onClick={close} className="absolute top-2 right-2 text-slate-400 hover:text-slate-700" data-testid="pwa-install-close" aria-label="Fermer">
        <X size={16}/>
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#E05638] grid place-items-center shrink-0"><Smartphone size={20}/></div>
        <div className="min-w-0">
          <div className="font-heading font-extrabold text-slate-900 text-sm">Installer PROXIreduc-28</div>
          {isIOS ? (
            <p className="mt-1 text-xs text-slate-600">
              Appuyez sur <b>Partager</b> dans Safari, puis <b>« Sur l'écran d'accueil »</b> pour accéder à votre Pass en 1 tap.
            </p>
          ) : (
            <p className="mt-1 text-xs text-slate-600">
              Ajoutez l'application à votre téléphone pour un accès immédiat à votre Pass, même hors ligne.
            </p>
          )}
          {!isIOS && deferred && (
            <button onClick={install} data-testid="pwa-install-btn"
                    className="mt-3 inline-flex items-center gap-1.5 bg-[#E05638] text-white font-bold text-xs px-3 py-1.5 rounded-full hover:bg-[#C13E1F]">
              <Download size={13}/> Installer l'app
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
